import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Mail, Phone, MapPin, Youtube, Send, Clock, ArrowRight } from 'lucide-react';

const ContactPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-orange py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-white/80 text-sm mb-6">
            <a href="/" className="hover:text-white">Home</a>
            <span>›</span>
            <span>Contact</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              We'd love to hear from you. Get in touch with us for any queries or support.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Cards */}
          <div className="lg:col-span-1 space-y-6">
            {/* Address Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-card hover:shadow-card-hover transition-all card-hover"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="text-primary" size={24} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Headquarters</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                15/287, Chopasani Housing Board,<br />
                Jodhpur, Rajasthan - 342008
              </p>
            </motion.div>

            {/* Phone Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-card hover:shadow-card-hover transition-all card-hover"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Phone className="text-primary" size={24} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Phone</h3>
              <a 
                href="tel:+919414134392" 
                className="text-primary hover:text-primary-600 font-medium"
              >
                +91 9414134392
              </a>
            </motion.div>

            {/* Email Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-card hover:shadow-card-hover transition-all card-hover"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <Mail className="text-primary" size={24} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">Email</h3>
              <a 
                href="mailto:brahmkhatrimahasabha@gmail.com" 
                className="text-primary hover:text-primary-600 font-medium text-sm break-all"
              >
                brahmkhatrimahasabha@gmail.com
              </a>
            </motion.div>

            {/* YouTube Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl border border-gray-100 p-6 shadow-card hover:shadow-card-hover transition-all card-hover"
            >
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                <Youtube className="text-red-600" size={24} />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-2">YouTube</h3>
              <a 
                href="https://www.youtube.com/@ParichaySangam2025" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-red-600 hover:text-red-700 font-medium text-sm"
              >
                @ParichaySangam2025
              </a>
            </motion.div>
          </div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl border border-gray-100 shadow-card overflow-hidden">
              {/* Form Header */}
              <div className="gradient-orange p-6">
                <h2 className="text-2xl font-bold text-white">Send us a message</h2>
                <p className="text-white/80 mt-1">Fill out the form below and we'll get back to you soon.</p>
              </div>
              
              {/* Form Body */}
              <div className="p-8">
                <form className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium mb-2 block">Name *</Label>
                      <Input 
                        id="name" 
                        type="text" 
                        required 
                        placeholder="Your full name"
                        className="h-12 border-gray-200 focus:border-primary focus:ring-primary"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block">Email *</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        required 
                        placeholder="your@email.com"
                        className="h-12 border-gray-200 focus:border-primary focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block">Phone</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="+91 XXXXXXXXXX"
                        className="h-12 border-gray-200 focus:border-primary focus:ring-primary"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject" className="text-gray-700 font-medium mb-2 block">Subject *</Label>
                      <Input 
                        id="subject" 
                        type="text" 
                        required 
                        placeholder="What is this about?"
                        className="h-12 border-gray-200 focus:border-primary focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-medium mb-2 block">Message *</Label>
                    <Textarea 
                      id="message" 
                      rows={5} 
                      required 
                      placeholder="Write your message here..."
                      className="border-gray-200 focus:border-primary focus:ring-primary resize-none"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-gray-500 flex items-center gap-2">
                      <Clock size={16} />
                      We typically respond within 24 hours
                    </p>
                    <Button 
                      type="submit" 
                      className="bg-primary hover:bg-primary-600 text-white font-semibold px-8 py-3 h-auto rounded-lg shadow-sm hover:shadow-md transition-all"
                    >
                      Send Message
                      <Send size={18} className="ml-2" />
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 section-heading">
            Frequently Asked Questions
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                question: 'How can I become a member?',
                answer: 'You can register online through our website by clicking on "Become a Member" button and filling out the registration form.'
              },
              {
                question: 'How do I apply for scholarship?',
                answer: 'Visit our Scholarships page and click on "Apply Now" to submit your scholarship application with all required documents.'
              },
              {
                question: 'How can I donate to the association?',
                answer: 'You can contribute through our Donor Program. Visit the donor registration page to make a contribution.'
              },
              {
                question: 'How do I update my member profile?',
                answer: 'Login to your member account and navigate to Profile section to update your personal information.'
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-gray-50 rounded-xl p-6 hover:bg-primary/5 transition-colors"
              >
                <h3 className="font-bold text-gray-900 mb-2">{faq.question}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default ContactPage;
